// src/pages/billing/BillingAccountListPage.tsx
// B2B2C billing accounts list. "+ New billing account" opens a create dialog
// in place (BillingAccountFormDialog) — see docs/ux/14-forms.md. Rows link to
// the inline-editable detail page; there is no /edit route.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingBillingAccount, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { BillingAccountFormDialog } from '@/features/billing/BillingAccountFormDialog';
import { formatMoney } from '@/features/billing/format';
import type { BillingAccount } from '@core/types/billing';

export const BillingAccountListPage = observer(() => {
  const store = useBillingBillingAccount();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    store.ListBillingAccounts({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
      view: 0,
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleCreated = (account: BillingAccount) => {
    setCreateOpen(false);
    if (account.entity?.id) {
      navigate(`/billing/accounts/${account.entity.id}`);
    } else {
      load();
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('billing.account.listTitle')}</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">{t('billing.title')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('billing.account.listTitle')}</span>
        </nav>
        <Button onClick={() => setCreateOpen(true)}>{t('billing.account.create')}</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {store.error ? (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      ) : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('billing.account.email')}</TableHead>
              <TableHead>{t('billing.account.balance')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={4} message={t('billing.account.empty')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link to={`/billing/accounts/${item.entity?.id || item.id}`} className="text-primary hover:underline">
                      {String(item.name ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>{String(item.email ?? '')}</TableCell>
                  <TableCell>{formatMoney(item.current_balance)}</TableCell>
                  <TableCell>
                    {item.is_delinquent ? (
                      <Badge variant="error" size="sm">{t('billing.account.delinquent')}</Badge>
                    ) : item.is_active ? (
                      <Badge variant="success" size="sm">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <BillingAccountFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onError={setError}
      />
    </div>
  );
});

export default BillingAccountListPage;
